import { ButtonBase, Chip, Typography } from '@material-ui/core'
import React from 'react'

import {
  assignmentLabel,
  formatWhen,
  isTerminal,
  needsAgentAttention,
  stateTone,
  ticketStateLabel,
  Ticket
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import useStyles from '../SupportTickets/styles/SupportTicketRowMobile.styles'

interface SupportQueueRowMobileProps {
  ticket: Ticket
  onClick: () => void
}

/**
 * The queue's own mobile card.
 *
 * It cannot reuse SupportTicketRowMobile: that renders affectedEntityRefs,
 * while the queue's desktop row shows the REPORTER entity and the "Needs
 * Adotel" escalation chip. Reusing it silently dropped both on a phone — so an
 * agent triaging the cross-tenant queue could not see who reported a ticket,
 * and could not see the signal the screen's own subtitle promises is
 * highlighted.
 *
 * The stylesheet IS shared, deliberately: the two cards are the same shape and
 * only their content differs. That is layout duplication avoided, not
 * behaviour — and it keeps the two cards visually identical, which is the
 * point of a card list.
 */
export default function SupportQueueRowMobile({
  ticket,
  onClick
}: SupportQueueRowMobileProps) {
  const classes = useStyles()
  const needsAttention = needsAgentAttention(ticket)

  return (
    <ButtonBase
      className={classes.root}
      style={{ opacity: isTerminal(ticket.state) ? 0.85 : 1 }}
      aria-label={`${ticket.ticketNumber}: ${ticket.title}`}
      onClick={onClick}>
      <div className={classes.primary}>
        <Typography variant="body2" color="textSecondary">
          <bdi dir="ltr">{ticket.ticketNumber}</bdi>
        </Typography>
        <Typography variant="subtitle2">{ticket.title}</Typography>
        <Typography variant="body2" color="textSecondary">
          {/* The reporter entity, which is the queue's whole reason to exist:
              it is the only column that says WHOSE ticket this is. */}
          {ticket.reporterEntityRef ? (
            <bdi dir="ltr">
              {`${ticket.reporterEntityRef.type}:${ticket.reporterEntityRef.code}`}
            </bdi>
          ) : (
            getTranslate('No reporter entity')
          )}
        </Typography>
      </div>
      <div className={classes.secondary}>
        <Chip
          size="small"
          label={ticketStateLabel(ticket.state)}
          color={stateTone(ticket.state)}
        />
        {needsAttention && (
          <Chip
            size="small"
            color="secondary"
            label={getTranslate('Needs Adotel')}
          />
        )}
        <Typography variant="caption" color="textSecondary">
          {assignmentLabel(ticket)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {formatWhen(ticket.changedAt)}
        </Typography>
      </div>
    </ButtonBase>
  )
}
